'use client'

import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Sparkles,
  Phone,
  MessageSquare,
  PhoneMissed,
  CarFront,
  FileText,
  RotateCcw,
  Check,
  ArrowRight,
} from 'lucide-react'

interface Capability {
  title: string
  description: string
}

interface AutomotiveAIShowcaseSectionProps {
  eyebrow?: string
  heading: string
  intro?: string
  capabilities: Capability[]
  proof?: {
    heading: string
    points: string[]
    linkLabel?: string
    linkHref?: string
  }
}

const ICONS = [Phone, MessageSquare, PhoneMissed, CarFront, FileText, RotateCcw]

export function AutomotiveAIShowcaseSection({
  eyebrow,
  heading,
  intro,
  capabilities,
  proof,
}: AutomotiveAIShowcaseSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-b from-background via-muted/20 to-background">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
            >
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="eyebrow">{eyebrow}</span>
            </motion.div>
          )}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
          >
            {heading}
          </motion.h2>
          {intro && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="reveal reveal-delay-2 text-lg text-muted-foreground"
            >
              {intro}
            </motion.p>
          )}
        </div>

        {/* Capability grid */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto mb-14">
          {capabilities.map((cap, index) => {
            const Icon = ICONS[index % ICONS.length]
            return (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="reveal group rounded-3xl border border-border/50 bg-white dark:bg-zinc-900 p-7 transition-all duration-300 lift-hover"
                style={{ transitionDelay: `${(index % 4) * 80}ms` }}
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#2F5FE8]/10 to-[#2F5FE8]/20 dark:from-[#2F5FE8]/20 dark:to-[#2F5FE8]/30 flex items-center justify-center mb-5 border border-[#2F5FE8]/20 dark:border-[#2F5FE8]/20">
                  <Icon className="h-6 w-6 text-[#2F5FE8] dark:text-[#7EA0F5]" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{cap.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{cap.description}</p>
              </motion.div>
            )
          })}
        </div>

        {/* Founder-built proof */}
        {proof && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto rounded-3xl border border-primary/20 bg-primary/5 p-8 md:p-10"
          >
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">
              Proof in production
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-6">{proof.heading}</h3>
            <ul className="grid gap-3 sm:grid-cols-2">
              {proof.points.map((point) => (
                <li key={point} className="flex items-start gap-2.5 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            {proof.linkLabel && proof.linkHref && (
              <Link
                to={proof.linkHref}
                className="mt-7 inline-flex items-center gap-1.5 text-sm md:text-base font-bold text-primary hover:gap-2.5 transition-all"
              >
                {proof.linkLabel}
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </motion.div>
        )}
      </div>
    </section>
  )
}
